import React, { useState } from 'react';

const HelpCenterPage: React.FC = () => {
  console.log('HelpCenterPage rendered - comprehensive help and support resources');

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Topics', icon: '📚' },
    { id: 'getting-started', name: 'Getting Started', icon: '🚀' },
    { id: 'lessons', name: 'Lessons & Booking', icon: '📅' },
    { id: 'payments', name: 'Payments & Billing', icon: '💳' },
    { id: 'technical', name: 'Technical Support', icon: '🔧' },
    { id: 'teachers', name: 'For Teachers', icon: '👨‍🏫' },
    { id: 'account', name: 'Account & Profile', icon: '👤' },
  ];

  const faqs = [
    {
      id: 1,
      category: 'getting-started',
      question: 'How do I find a music teacher on LessonLynx?',
      answer: 'Getting started is easy! Click "Find a Teacher" and tell us what instrument you want to learn. We\'ll ask a few questions about your goals, experience level, and preferences, then match you with compatible teachers in your area or online.',
      popular: true
    },
    {
      id: 2,
      category: 'getting-started',
      question: 'What instruments can I learn on LessonLynx?',
      answer: 'We offer lessons for a wide variety of instruments including piano, guitar, violin, drums, saxophone, voice/singing, ukulele, bass guitar, flute, clarinet, trumpet, and many more. If you don\'t see your instrument listed, contact us and we\'ll help you find a teacher.',
      popular: true
    },
    {
      id: 3,
      category: 'lessons',
      question: 'Can I take lessons online or in-person?',
      answer: 'Yes! Many of our teachers offer both online and in-person lessons. You can specify your preference when searching for teachers. Online lessons are conducted through video calling platforms, while in-person lessons can be at the teacher\'s studio, your home, or a mutually agreed location.',
      popular: true
    },
    {
      id: 4,
      category: 'lessons',
      question: 'How do I book and schedule lessons?',
      answer: 'Once you\'ve chosen a teacher, you can view their availability and book lessons directly through our platform. Simply select your preferred time slots, choose between 30-minute or 60-minute sessions, and confirm your booking. You\'ll receive confirmation via email.',
      popular: false
    },
    {
      id: 5,
      category: 'lessons',
      question: 'What if I need to cancel or reschedule a lesson?',
      answer: 'You can cancel or reschedule lessons up to 24 hours in advance at no charge. For cancellations with less than 24 hours notice, a cancellation fee may apply. Emergency cancellations are handled case-by-case.',
      popular: false
    },
    {
      id: 6,
      category: 'payments',
      question: 'How much do lessons cost?',
      answer: 'Lesson prices range from $30-$60 per session depending on the teacher and lesson length. We offer trial bundles (3 lessons for $100), monthly packages for 30-minute lessons ($120/month), and 60-minute lessons ($240/month). Check our Pricing page for full details.',
      popular: true
    },
    {
      id: 7,
      category: 'payments',
      question: 'How do payments work?',
      answer: 'All payments are processed securely through our platform. You can pay for individual lessons or purchase monthly packages. We accept all major credit cards and payments are automatically processed according to your chosen plan.',
      popular: false
    },
    {
      id: 8,
      category: 'payments',
      question: 'Can I get a refund if I\'m not satisfied?',
      answer: 'We want you to be happy with your lessons! If you\'re not satisfied with your first lesson, contact us within 48 hours and we\'ll work with you to find a solution, which may include a refund or matching you with a different teacher.',
      popular: false
    },
    {
      id: 9,
      category: 'teachers',
      question: 'How do I become a teacher on LessonLynx?',
      answer: 'We\'d love to have you join our community! Visit our "For Teachers" page to learn about our commission structure and teacher benefits, then complete the application process. You\'ll need to provide information about your musical background, teaching experience, and complete a brief verification process.',
      popular: true
    },
    {
      id: 10,
      category: 'teachers',
      question: 'What percentage does LessonLynx take from lessons?',
      answer: 'Our commission structure is tiered and decreases as you gain more students. We take 5-10% plus Stripe processing fees (3%). The more students you have, the lower our percentage. Visit our For Teachers page to see the full commission structure.',
      popular: true
    },
    {
      id: 11,
      category: 'teachers',
      question: 'How do I get paid as a teacher?',
      answer: 'Teachers are paid automatically after each completed lesson. Payments are processed through Stripe and typically arrive in your bank account within 2-3 business days. You can track all your earnings through your teacher dashboard.',
      popular: false
    },
    {
      id: 12,
      category: 'technical',
      question: 'What do I need for online lessons?',
      answer: 'For online lessons, you\'ll need a reliable internet connection, a device with a camera and microphone (computer, tablet, or smartphone), and your instrument. We recommend using headphones for better audio quality and ensuring good lighting.',
      popular: false
    },
    {
      id: 13,
      category: 'technical',
      question: 'I\'m having trouble with the website. Who can help?',
      answer: 'If you\'re experiencing technical issues, try refreshing the page or clearing your browser cache first. If problems persist, contact our support team through the Contact Us page or email hello@lessonlynx.com with details about the issue.',
      popular: false
    },
    {
      id: 14,
      category: 'account',
      question: 'How do I update my profile information?',
      answer: 'You can update your profile information by logging into your account and visiting your Dashboard. Click on "Profile" or "Account Settings" to edit your personal information, preferences, and contact details.',
      popular: false
    },
    {
      id: 15,
      category: 'account',
      question: 'I forgot my password. How do I reset it?',
      answer: 'Click the "Forgot Password" link on the login page and enter your email address. We\'ll send you a password reset link. If you don\'t receive the email, check your spam folder or contact support.',
      popular: false
    }
  ];

  const filteredFaqs = faqs.filter(faq => {
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    const matchesSearch = searchQuery === '' || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const popularFaqs = faqs.filter(faq => faq.popular);

  const quickStartGuides = [
    {
      title: 'Student Quick Start',
      description: 'Learn how to find your perfect teacher and book your first lesson',
      steps: [
        'Click "Find a Teacher" and select your instrument',
        'Answer a few questions about your goals and experience',
        'Browse matched teachers and read their profiles',
        'Book a trial lesson or monthly package',
        'Attend your lesson and start learning!'
      ],
      icon: '🎓'
    },
    {
      title: 'Teacher Quick Start',
      description: 'Get started teaching on LessonLynx and building your studio',
      steps: [
        'Visit "For Teachers" page and complete the application',
        'Set up your profile with photos and teaching information',
        'Configure your availability and pricing',
        'Get verified and approved by our team',
        'Start receiving student bookings!'
      ],
      icon: '🎵'
    }
  ];

  return (
    <div className="bg-white text-gray-800">
      {/* Hero Section */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-6">
            <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl md:text-6xl mb-6">
            Help Center
          </h1>
          <p className="mt-4 max-w-3xl mx-auto text-xl sm:text-2xl text-gray-700 leading-relaxed">
            Find answers to your questions and get the help you need
          </p>
          
          {/* Search Bar */}
          <div className="mt-8 max-w-lg mx-auto">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search for help articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder-gray-500"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Quick Start Guides */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Quick Start Guides
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              New to LessonLynx? These step-by-step guides will get you started
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {quickStartGuides.map((guide, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-8 border border-gray-200">
                <div className="flex items-center mb-4">
                  <div className="text-3xl mr-4">{guide.icon}</div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">{guide.title}</h3>
                    <p className="text-gray-600">{guide.description}</p>
                  </div>
                </div>
                <ol className="space-y-3">
                  {guide.steps.map((step, stepIndex) => (
                    <li key={stepIndex} className="flex items-start">
                      <span className="bg-blue-600 text-white text-sm font-medium w-6 h-6 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                        {stepIndex + 1}
                      </span>
                      <span className="text-gray-700">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular FAQs */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Popular Questions
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              The most frequently asked questions from our community
            </p>
          </div>
          
          <div className="space-y-6">
            {popularFaqs.map((faq) => (
              <div key={faq.id} className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                    <p className="text-gray-700">{faq.answer}</p>
                  </div>
                  <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-1 rounded-full ml-4 flex-shrink-0">
                    Popular
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Category Navigation */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Browse by Category
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Find answers organized by topic
            </p>
          </div>
          
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`inline-flex items-center px-4 py-2 rounded-lg border transition-colors ${
                  selectedCategory === category.id
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                <span className="mr-2">{category.icon}</span>
                {category.name}
              </button>
            ))}
          </div>

          {/* FAQ List */}
          <div className="space-y-6">
            {filteredFaqs.length > 0 ? (
              filteredFaqs.map((faq) => (
                <div key={faq.id} className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 20.4a7.962 7.962 0 01-2.34-.349c-1.357-.257-2.624-1.055-3.598-2.323L4.5 19.5l1.772-1.228A7.942 7.942 0 014 12c0-4.418 3.582-8 8-8s8 3.582 8 8c0 1.819-.616 3.498-1.65 4.838l1.228 1.772-1.772 1.228c-1.268-1.974-3.066-2.771-4.423-3.028z" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
                <p className="text-gray-600">Try adjusting your search or browse a different category.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-gradient-to-br from-blue-600 to-indigo-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-extrabold text-white sm:text-4xl mb-6">
            Still Need Help?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Can't find what you're looking for? Our support team is here to help you succeed.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/contact" 
              className="inline-flex items-center justify-center px-8 py-4 border border-transparent text-lg font-medium rounded-lg text-blue-700 bg-white hover:bg-gray-50 transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              Contact Support
              <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </a>
            <a 
              href="mailto:hello@lessonlynx.com" 
              className="inline-flex items-center justify-center px-8 py-4 border border-white text-lg font-medium rounded-lg text-white hover:bg-white hover:text-blue-700 transition-colors duration-200"
            >
              Email Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HelpCenterPage; 